import {
  configurePages,
  createPathRegex,
  type InferredPage,
  type InferredPageId,
  type PageConfiguration,
} from '@/src/lib/pages';

export enum PageAuthType {
  Auth0 = 'auth0',
  Lti = 'lti',
  Public = 'public',
}

type PageHelperInput = Omit<PageConfiguration, 'id' | 'isLti' | 'isPublic'>;

type PublicPageOutput<Id extends string, T extends PageHelperInput> = {
  readonly auth: PageAuthType.Public;
  readonly id: Id;
  readonly isAuth0: false;
  readonly isLti: false;
  readonly isPublic: true;
} & T;

type Auth0ProtectedPageOutput<Id extends string, T extends PageHelperInput> = {
  readonly auth: PageAuthType.Auth0;
  readonly id: Id;
  readonly isAuth0: true;
  readonly isLti: false;
  readonly isPublic: false;
} & T;

type LtiProtectedPageOutput<Id extends string, T extends PageHelperInput> = {
  readonly auth: PageAuthType.Lti;
  readonly id: Id;
  readonly isAuth0: false;
  readonly isLti: true;
  readonly isPublic: false;
} & T;

/**
 * Constructs a {@link PublicPageOutput} entry, i.e. a {@link PageConfiguration} whose `isPublic`
 * flag is set to `true`, used when declaring an unauthenticated page in {@link EdgeSafePages}.
 */
const PublicPage = <I extends string, T extends PageHelperInput>(
  id: I,
  config: T,
): PublicPageOutput<I, T> => ({
  ...config,
  auth: PageAuthType.Public,
  id,
  isAuth0: false,
  isLti: false,
  isPublic: true,
});

/**
 * Constructs a {@link Auth0ProtectedPage} entry, i.e. a {@link PageConfiguration} whose
 * `isPublic` flag is set to `false`, used when declaring an authenticated page in
 * {@link EdgeSafePages}.
 */
const Auth0ProtectedPage = <I extends string, T extends PageHelperInput>(
  id: I,
  config: T,
): Auth0ProtectedPageOutput<I, T> => ({
  ...config,
  auth: PageAuthType.Auth0,
  id,
  isAuth0: true,
  isLti: false,
  isPublic: false,
});

/**
 * Constructs an {@link LtiProtectedPageOutput} entry, i.e. a {@link PageConfiguration} whose
 * `isLti` flag is set to `true`, used when declaring an LTI-launched page in {@link EdgeSafePages}.
 *
 * LTI pages do not authenticate via Auth0; they validate their own LTI session inside the page's
 * `getServerSideProps`. The middleware therefore skips the Auth0 session check for these pages
 * (treating them as if they were public for routing purposes), but `isPublic` remains `false` so
 * that the application still renders them with the authenticated layout rather than the public
 * layout.
 */
const LtiPage = <I extends string, T extends PageHelperInput>(
  id: I,
  config: T,
): LtiProtectedPageOutput<I, T> => ({
  ...config,
  auth: PageAuthType.Lti,
  id,
  isAuth0: false,
  isLti: true,
  isPublic: false,
});

/**
 * The application's edge-safe pages configuration, {@link IPagesConfiguration}, which declares
 * every page that this application renders along with the minimum {@link PageConfiguration}
 * metadata needed to identify and route to it (`id`, `path`, `matcher`, and `isPublic`).
 *
 * The configuration deliberately omits any UI-aware fields ({@link PageLayoutVersion}, document
 * title, active-state matcher, etc.) so that it can be consumed safely by code that runs in the
 * edge runtime, where browser and React APIs are unavailable.
 *
 * Only the edge middleware should consume {@link EdgeSafePages}. Any code running inside the
 * Next.js page lifecycle (e.g. {@link getServerSideProps} handlers, page components, React
 * components, and hooks) should consume the richer {@link Pages} configuration instead.
 *
 * @see {@link configurePages}
 * @see {@link Pages}
 */
export const EdgeSafePages = configurePages([
  Auth0ProtectedPage('absences-report', {
    matcher: createPathRegex('/reports/absences', { exact: true }),
    path: '/reports/absences',
  } as const),
  Auth0ProtectedPage('activity', {
    matcher: createPathRegex(
      '/organizationPrograms/:programId/levels/:levelId/plans/:planId/activities/:activityId',
      { exact: true },
    ),
    path: '/organizationPrograms/:programId/levels/:levelId/plans/:planId/activities/:activityId',
  } as const),
  Auth0ProtectedPage('activity-assigned-learners', {
    matcher: createPathRegex(
      '/organizationPrograms/:programId/levels/:levelId/plans/:planId/activities/:activityId/assigned-learners',
      { exact: true },
    ),
    path: '/organizationPrograms/:programId/levels/:levelId/plans/:planId/activities/:activityId/assigned-learners',
  } as const),
  Auth0ProtectedPage('add-sponsored-organization', {
    matcher: createPathRegex('/organizations/sponsored/add', { exact: true }),
    path: '/organizations/sponsored/add',
  } as const),
  Auth0ProtectedPage('add-system-organization', {
    matcher: createPathRegex('/organizations/system/add', { exact: true }),
    path: '/organizations/system/add',
  } as const),
  Auth0ProtectedPage('add-user', {
    matcher: createPathRegex('/manageUsers/add', { exact: true }),
    path: '/manageUsers/add',
  } as const),
  Auth0ProtectedPage('ai-rubric-import', {
    matcher: createPathRegex('/ai-rubric-import', { exact: true }),
    path: '/ai-rubric-import',
  } as const),
  Auth0ProtectedPage('archived-learner-cohorts', {
    matcher: createPathRegex('/cohorts/archived', { exact: true }),
    path: '/cohorts/archived',
  } as const),
  Auth0ProtectedPage('archived-programs', {
    matcher: createPathRegex('/organizationPrograms/archived', { exact: true }),
    path: '/organizationPrograms/archived',
  } as const),
  Auth0ProtectedPage('assign-evaluators', {
    matcher: createPathRegex('/organizationPrograms/:programId/assign-evaluators', { exact: true }),
    path: '/organizationPrograms/:programId/assign-evaluators',
  } as const),
  Auth0ProtectedPage('assign-learners', {
    matcher: createPathRegex('/organizationPrograms/:programId/assign-learners', { exact: true }),
    path: '/organizationPrograms/:programId/assign-learners',
  } as const),
  Auth0ProtectedPage('assign-plan', {
    matcher: createPathRegex(
      '/organizationPrograms/:programId/levels/:levelId/plans/:planId/assign',
      { exact: true },
    ),
    path: '/organizationPrograms/:programId/levels/:levelId/plans/:planId/assign',
  } as const),
  Auth0ProtectedPage('assign-rap', {
    matcher: createPathRegex('/raps/:rapId/assign', { exact: true }),
    path: '/raps/:rapId/assign',
  } as const),
  Auth0ProtectedPage('assigned-activity', {
    matcher: createPathRegex('/assignedPrograms/assignedActivity/:assignedActivityId', {
      exact: true,
    }),
    path: '/assignedPrograms/assignedActivity/:assignedActivityId',
  } as const),
  Auth0ProtectedPage('assigned-level', {
    matcher: createPathRegex('/assignedPrograms/assignedLevel/:assignedLevelId', { exact: true }),
    path: '/assignedPrograms/assignedLevel/:assignedLevelId',
  } as const),
  Auth0ProtectedPage('assigned-plan', {
    matcher: createPathRegex('/assignedPrograms/assignedPlan/:assignedPlanId', { exact: true }),
    path: '/assignedPrograms/assignedPlan/:assignedPlanId',
  } as const),
  Auth0ProtectedPage('assigned-program', {
    matcher: createPathRegex('/assignedPrograms/assignedProgram/:assignedProgramId', {
      exact: true,
    }),
    path: '/assignedPrograms/assignedProgram/:assignedProgramId',
  } as const),
  Auth0ProtectedPage('assigned-programs', {
    matcher: createPathRegex('/assignedPrograms', { exact: true }),
    path: '/assignedPrograms',
  } as const),
  Auth0ProtectedPage('batch-configure', {
    matcher: createPathRegex('/organizationPrograms/:programId/batch-configure', { exact: true }),
    path: '/organizationPrograms/:programId/batch-configure',
  } as const),
  Auth0ProtectedPage('configure-observations', {
    matcher: createPathRegex('/organizationPrograms/:programId/configure-observations', {
      exact: true,
    }),
    path: '/organizationPrograms/:programId/configure-observations',
  } as const),
  Auth0ProtectedPage('coursework', {
    matcher: createPathRegex('/organizationPrograms/:programId/coursework', { exact: true }),
    path: '/organizationPrograms/:programId/coursework',
  } as const),
  Auth0ProtectedPage('craft-admin', {
    matcher: createPathRegex('/craftadmin', { exact: true }),
    path: '/craftadmin',
  } as const),
  Auth0ProtectedPage('dashboard', {
    matcher: createPathRegex('/dashboard', { exact: true }),
    path: '/dashboard',
  } as const),
  PublicPage('docs-swagger', {
    matcher: createPathRegex('/docs/swagger', { exact: true }),
    path: '/docs/swagger',
  } as const),
  Auth0ProtectedPage('edlink', {
    matcher: createPathRegex('/edlink', { exact: true }),
    path: '/edlink',
  } as const),
  Auth0ProtectedPage('edlink-admin-organization-scope', {
    matcher: createPathRegex('/edlink-admin-organization-scope', { exact: true }),
    path: '/edlink-admin-organization-scope',
  } as const),
  Auth0ProtectedPage('program-edlink', {
    matcher: createPathRegex('/organizationPrograms/:programId/edlink', { exact: true }),
    path: '/organizationPrograms/:programId/edlink',
  } as const),
  Auth0ProtectedPage('home', {
    matcher: createPathRegex('/', { exact: true }),
    path: '/',
  } as const),
  Auth0ProtectedPage('learner', {
    matcher: createPathRegex('/learner/:learnerId', { exact: true }),
    path: '/learner/:learnerId',
  } as const),
  Auth0ProtectedPage('learner-cohort', {
    matcher: createPathRegex('/cohorts/:cohortId', {
      exact: true,
      excludePathParams: ['archived'],
    }),
    path: '/cohorts/:cohortId',
  } as const),
  Auth0ProtectedPage('learner-cohorts', {
    matcher: createPathRegex('/cohorts', { exact: true }),
    path: '/cohorts',
  } as const),
  Auth0ProtectedPage('learners', {
    matcher: createPathRegex('/learners', { exact: true }),
    path: '/learners',
  } as const),
  Auth0ProtectedPage('level', {
    matcher: createPathRegex('/organizationPrograms/:programId/levels/:levelId', { exact: true }),
    path: '/organizationPrograms/:programId/levels/:levelId',
  } as const),
  Auth0ProtectedPage('level-assigned-learners', {
    matcher: createPathRegex('/organizationPrograms/:programId/levels/:levelId/assigned-learners', {
      exact: true,
    }),
    path: '/organizationPrograms/:programId/levels/:levelId/assigned-learners',
  } as const),
  LtiPage('lti-select-activity', {
    matcher: createPathRegex('/lti/select-activity', { exact: true }),
    path: '/lti/select-activity',
  } as const),
  LtiPage('lti-show-activity', {
    matcher: createPathRegex('/lti/show-activity', { exact: true }),
    path: '/lti/show-activity',
  } as const),
  LtiPage('lti-speed-grader', {
    matcher: createPathRegex('/lti/speed-grader', { exact: true }),
    path: '/lti/speed-grader',
  } as const),
  LtiPage('lti-submit-activity', {
    matcher: createPathRegex('/lti/submit-activity', { exact: true }),
    path: '/lti/submit-activity',
  } as const),
  Auth0ProtectedPage('manage-user', {
    matcher: createPathRegex('/manageUsers/:userId', {
      exact: true,
      excludePathParams: ['add', 'tags'],
    }),
    path: '/manageUsers/:userId',
  } as const),
  Auth0ProtectedPage('manage-user-tags', {
    matcher: createPathRegex('/manageUsers/tags', { exact: true }),
    path: '/manageUsers/tags',
  } as const),
  Auth0ProtectedPage('manage-users', {
    matcher: createPathRegex('/manageUsers', { exact: true }),
    path: '/manageUsers',
  } as const),
  Auth0ProtectedPage('my-profile', {
    matcher: createPathRegex('/myProfile', { exact: true }),
    path: '/myProfile',
  } as const),
  PublicPage('not-found', {
    matcher: createPathRegex('/404', { exact: true }),
    path: '/404',
  } as const),
  Auth0ProtectedPage('pending', {
    matcher: createPathRegex('/pending', { exact: true }),
    path: '/pending',
  } as const),
  Auth0ProtectedPage('pending-approvals-activities', {
    matcher: createPathRegex('/pending/activities', { exact: true }),
    path: '/pending/activities',
  } as const),
  Auth0ProtectedPage('pending-approvals-not-configured', {
    matcher: createPathRegex('/pending/not-configured', { exact: true }),
    path: '/pending/not-configured',
  } as const),
  Auth0ProtectedPage('pending-approvals-observations', {
    matcher: createPathRegex('/pending/observations', { exact: true }),
    path: '/pending/observations',
  } as const),
  Auth0ProtectedPage('pending-approvals-time-entries', {
    matcher: createPathRegex('/pending/time-entries', { exact: true }),
    path: '/pending/time-entries',
  } as const),
  Auth0ProtectedPage('plan', {
    matcher: createPathRegex('/organizationPrograms/:programId/levels/:levelId/plans/:planId', {
      exact: true,
    }),
    path: '/organizationPrograms/:programId/levels/:levelId/plans/:planId',
  } as const),
  Auth0ProtectedPage('plan-assigned-learners', {
    matcher: createPathRegex(
      '/organizationPrograms/:programId/levels/:levelId/plans/:planId/assigned-learners',
      { exact: true },
    ),
    path: '/organizationPrograms/:programId/levels/:levelId/plans/:planId/assigned-learners',
  } as const),
  Auth0ProtectedPage('program', {
    matcher: createPathRegex('/organizationPrograms/:programId', {
      exact: true,
      excludePathParams: ['add', 'sponsored', 'archived'],
    }),
    path: '/organizationPrograms/:programId',
  } as const),
  Auth0ProtectedPage('program-assigned-learners', {
    matcher: createPathRegex('/organizationPrograms/:programId/assigned-learners', {
      exact: true,
    }),
    path: '/organizationPrograms/:programId/assigned-learners',
  } as const),
  Auth0ProtectedPage('programs', {
    matcher: createPathRegex('/organizationPrograms', { exact: true }),
    path: '/organizationPrograms',
  } as const),
  Auth0ProtectedPage('rap', {
    matcher: createPathRegex('/raps/:rapId', { exact: true }),
    path: '/raps/:rapId',
  } as const),
  Auth0ProtectedPage('rap-assigned-learners', {
    matcher: createPathRegex('/raps/:rapId/assigned-learners', { exact: true }),
    path: '/raps/:rapId/assigned-learners',
  } as const),
  Auth0ProtectedPage('rap-competencies', {
    matcher: createPathRegex('/raps/:rapId/competencies', { exact: true }),
    path: '/raps/:rapId/competencies',
  } as const),
  Auth0ProtectedPage('rap-level', {
    matcher: createPathRegex('/raps/:rapId/level/:assignedLevelId', { exact: true }),
    path: '/raps/:rapId/level/:assignedLevelId',
  } as const),
  Auth0ProtectedPage('rap-plan', {
    matcher: createPathRegex('/raps/:rapId/plan/:assignedPlanId', { exact: true }),
    path: '/raps/:rapId/plan/:assignedPlanId',
  } as const),
  Auth0ProtectedPage('rap-program', {
    matcher: createPathRegex('/raps/:rapId/program', { exact: true }),
    path: '/raps/:rapId/program',
  } as const),
  Auth0ProtectedPage('raps', {
    matcher: createPathRegex('/raps', { exact: true }),
    path: '/raps',
  } as const),
  Auth0ProtectedPage('release', {
    matcher: createPathRegex('/releases/:releaseVersion', { exact: true }),
    path: '/releases/:releaseVersion',
  } as const),
  Auth0ProtectedPage('releases', {
    matcher: createPathRegex('/releases', { exact: true }),
    path: '/releases',
  } as const),
  Auth0ProtectedPage('thoughtspot-report', {
    matcher: createPathRegex('/reports/thoughtspot/:reportSlug', {
      exact: true,
    }),
    path: '/reports/thoughtspot/:reportSlug',
  } as const),
  Auth0ProtectedPage('metabase-report', {
    matcher: createPathRegex('/reports/metabase/:dashboardId', {
      exact: true,
    }),
    path: '/reports/metabase/:dashboardId',
  } as const),
  Auth0ProtectedPage('reports', {
    matcher: createPathRegex('/reports', { exact: true }),
    path: '/reports',
  } as const),
  Auth0ProtectedPage('rubric', {
    matcher: createPathRegex('/rubrics/:rubricId', {
      exact: true,
      excludePathParams: ['ai-import'],
    }),
    path: '/rubrics/:rubricId',
  } as const),
  Auth0ProtectedPage('rubrics', {
    matcher: createPathRegex('/rubrics', { exact: true }),
    path: '/rubrics',
  } as const),
  PublicPage('server-error', {
    matcher: createPathRegex('/500', { exact: true }),
    path: '/500',
  } as const),
  Auth0ProtectedPage('settings', {
    matcher: createPathRegex('/settings', { exact: true }),
    path: '/settings',
  } as const),
  Auth0ProtectedPage('sponsored-organizations', {
    matcher: createPathRegex('/organizations/sponsored', { exact: true }),
    path: '/organizations/sponsored',
  } as const),
  Auth0ProtectedPage('sponsored-programs', {
    matcher: createPathRegex('/organizationPrograms/sponsored', { exact: true }),
    path: '/organizationPrograms/sponsored',
  } as const),
  Auth0ProtectedPage('support', {
    matcher: createPathRegex('/support', { exact: true }),
    path: '/support',
  } as const),
  Auth0ProtectedPage('switch-view', {
    matcher: createPathRegex('/switchView', { exact: true }),
    path: '/switchView',
  } as const),
  Auth0ProtectedPage('system-organizations', {
    matcher: createPathRegex('/organizations/system', { exact: true }),
    path: '/organizations/system',
  } as const),
  Auth0ProtectedPage('unreachable-accounts', {
    matcher: createPathRegex('/unreachableAccounts', { exact: true }),
    path: '/unreachableAccounts',
  } as const),
  PublicPage('welcome', {
    matcher: createPathRegex('/welcome', { exact: true }),
    path: '/welcome',
  } as const),
] as const);

/**
 * Resolves to the {@link EdgeSafePages} entry with the provided {@link EdgeSafePageId}, or to the
 * union of all entries when {@link I} is left as the default.
 */
export type EdgeSafePage<I extends EdgeSafePageId = EdgeSafePageId> = Extract<
  InferredPage<typeof EdgeSafePages>,
  { id: I }
>;

/**
 * The union of every page `id` declared in {@link EdgeSafePages}.
 */
export type EdgeSafePageId = InferredPageId<typeof EdgeSafePages>;

export const isPageIdForAuthType = <Auth extends PageAuthType>(
  id: unknown,
  auth: Auth,
): id is Extract<EdgeSafePage, { auth: Auth }>['id'] =>
  EdgeSafePages.isPageId(id) && EdgeSafePages.get(id).auth === auth;

/**
 * Type guard that returns whether the provided value is a {@link PublicPageId}, i.e. an
 * {@link EdgeSafePageId} that resolves to a {@link PageConfiguration} whose `isPublic` flag is
 * `true`.
 */
export const isPublicPageId = (id: unknown): id is PublicPageId =>
  isPageIdForAuthType(id, PageAuthType.Public);

/**
 * Type guard that returns whether the provided value is an {@link LtiPageId}, i.e. an
 * {@link EdgeSafePageId} that resolves to a {@link PageConfiguration} whose `isLti` flag is
 * `true`.
 */
export const isLtiPageId = (id: unknown): id is LtiPageId =>
  isPageIdForAuthType(id, PageAuthType.Lti);

export const isAuth0AuthenticatedPageId = (id: unknown): id is Auth0AuthenticatedPageId =>
  isPageIdForAuthType(id, PageAuthType.Auth0);

/**
 * The subset of {@link EdgeSafePageId}s whose corresponding {@link PageConfiguration} has its
 * `isPublic` flag set to `true`.
 */
export type PublicPageId = Extract<EdgeSafePage, { auth: PageAuthType.Public }>['id'];

/**
 * The subset of {@link EdgeSafePageId}s whose corresponding {@link PageConfiguration} has its
 * `isLti` flag set to `true`. LTI pages are not publicly renderable but bypass the Auth0 session
 * check in the edge middleware because they validate their own LTI session.
 */
export type LtiPageId = Extract<EdgeSafePage, { auth: PageAuthType.Lti }>['id'];

/**
 * The subset of {@link EdgeSafePageId}s whose corresponding {@link PageConfiguration} has its
 * `isPublic` flag set to `false` and its `isLti` flag set to `false`, i.e. pages that are
 * authenticated via Auth0.
 */
export type Auth0AuthenticatedPageId = Extract<EdgeSafePage, { auth: PageAuthType.Auth0 }>['id'];
